import * as React from "react";
import { Button, Modal } from "antd";
import { Agent, BootstrapConfig } from "../../schema/types/agent/entity";
import { CodeEditor } from "../group/identityMatcher/codeEditor";
import { useAppSelector } from "../../store/hooks";
import { selectContext } from "../../store/contextSlice";
import { AgentScriptTabs } from "./agentScriptTabs";
import { ImportButtonStandalone } from "../../components/import";

interface Props {
  agent: Agent;
  visible: boolean;
  onClose: () => void;
}

export const DownloadScriptsModal: React.FC<Props> = ({ agent, visible, onClose }) => {
  const { org } = useAppSelector(selectContext);

  const [step, setStep] = React.useState(1);
  const [bootstrapConfigText, setBootstrapConfigText] = React.useState("");
  const [bootstrapConfig, setBootstrapConfig] = React.useState<BootstrapConfig | null>(null);
  const [configError, setConfigError] = React.useState<string>("");

  function validateAndParseConfig(text: string): { valid: boolean; config: BootstrapConfig | null; error: string } {
    if (!text.trim()) {
      return { valid: false, config: null, error: "Bootstrap config is required" };
    }

    try {
      const parsed = JSON.parse(text);

      if (!parsed.registrationToken || !parsed.agentId || !parsed.agentLink || !parsed.hubEndpoint) {
        return {
          valid: false,
          config: null,
          error:
            "Invalid bootstrap config: missing required fields (registrationToken, agentId, agentLink, hubEndpoint)",
        };
      }

      return { valid: true, config: parsed as BootstrapConfig, error: "" };
    } catch {
      return { valid: false, config: null, error: "Invalid JSON format" };
    }
  }

  function handleConfigChange(text: string) {
    setBootstrapConfigText(text);
    const { valid, config, error } = validateAndParseConfig(text);
    setConfigError(error);
    setBootstrapConfig(valid ? config : null);
  }

  function handleFileImport(content: string) {
    handleConfigChange(content);
  }

  // Reset state when modal closes
  React.useEffect(() => {
    if (!visible) {
      setStep(1);
      setBootstrapConfigText("");
      setBootstrapConfig(null);
      setConfigError("");
    }
  }, [visible]);

  return (
    <Modal
      title={step === 1 ? "Provide Bootstrap Config" : "Download Scripts"}
      open={visible}
      maskClosable={false}
      onCancel={onClose}
      destroyOnHidden
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
        content: { minWidth: 600, maxWidth: "90vw" },
      }}
      width={1200}
      footer={
        <div className="flex items-center justify-between">
          <div>{step === 2 && <Button onClick={() => setStep(1)}>Back</Button>}</div>
          <div className="flex items-center gap-4">
            {step === 1 ? (
              <>
                <Button onClick={onClose}>Cancel</Button>
                <Button type="primary" onClick={() => setStep(2)} disabled={!bootstrapConfig}>
                  Next
                </Button>
              </>
            ) : (
              <Button type="primary" onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step === 1 ? (
        <div className="p-4">
          <div className="mb-4">
            <div className="mb-2">Paste or import your agent bootstrap config JSON:</div>
            <ImportButtonStandalone
              onImport={handleFileImport}
              accept=".json,application/json"
              existingHasContent={!!bootstrapConfigText.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div>JSON bootstrap config file</div>
                  <div className="mt-1 opacity-75">Content will replace existing data</div>
                </div>
              }
            />
          </div>
          <CodeEditor
            value={bootstrapConfigText}
            setValue={handleConfigChange}
            language="json"
            lint
            options={{
              basicSetup: { lineNumbers: true },
              style: { maxHeight: "calc(70vh - 180px)", overflow: "auto" },
            }}
            minHeight="200px"
          />
          {configError && <div className="text-color-danger text-sm mt-2">{configError}</div>}
        </div>
      ) : (
        <div className="h-full p-4">
          <AgentScriptTabs org={org} agentName={agent.name} bootstrapConfig={bootstrapConfig!} />
        </div>
      )}
    </Modal>
  );
};
